import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Camera, Upload, Mic, Volume2, Pause, Play, Square, Sparkles, X, Loader2, RotateCcw, ListMusic, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ReaderQueue from './ReaderQueue';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 9 OpenAI voices we expose. Curated kawaii Swedish-friendly labels.
const OPENAI_VOICES = [
  { id: 'shimmer', label: 'Shimmer', desc: 'Ljus & glad' },
  { id: 'nova',    label: 'Nova',    desc: 'Pigg & energisk' },
  { id: 'coral',   label: 'Coral',   desc: 'Varm & vänlig' },
  { id: 'sage',    label: 'Sage',    desc: 'Mjuk & klok' },
  { id: 'fable',   label: 'Fable',   desc: 'Berättande' },
  { id: 'alloy',   label: 'Alloy',   desc: 'Neutral' },
  { id: 'echo',    label: 'Echo',    desc: 'Lugn' },
  { id: 'ash',     label: 'Ash',     desc: 'Tydlig' },
  { id: 'onyx',    label: 'Onyx',    desc: 'Djup' },
];

const SPEEDS = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

// Compress an image File to JPEG ≤ 1600px max edge, return data URL
async function compressImage(file, maxEdge = 1600, quality = 0.85) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export default function ReaderPage() {
  const [mode, setMode] = useState(() => localStorage.getItem('reader_mode') || 'single');
  useEffect(() => { localStorage.setItem('reader_mode', mode); }, [mode]);

  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [busyOcr, setBusyOcr] = useState(false);
  const [busyTts, setBusyTts] = useState(false);

  const [voiceMode, setVoiceMode] = useState('openai'); // 'browser' | 'openai'
  const [openaiVoice, setOpenaiVoice] = useState('shimmer');
  const [openaiModel, setOpenaiModel] = useState('tts-1');
  const [speed, setSpeed] = useState(1.0);

  // OpenAI audio playback state
  const audioRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);

  // Browser TTS state
  const [browserSpeaking, setBrowserSpeaking] = useState(false);

  // Camera modal
  const [cameraOpen, setCameraOpen] = useState(false);

  const fileInputRef = useRef(null);

  // Keep speed in sync on the audio element
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, audioUrl]);

  // Cleanup blob URL on unmount/replacement
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const stopAllAudio = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); }
    setPlaying(false);
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setBrowserSpeaking(false);
  }, []);

  // ---------------- Image input ----------------
  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      toast.error('Bara JPG, PNG eller WEBP funkar 🌸');
      return;
    }
    try {
      const compressed = await compressImage(file);
      setImageDataUrl(compressed);
      setExtractedText('');
      stopAllAudio();
      if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
    } catch (err) {
      toast.error('Kunde inte läsa bilden');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onCameraCaptured = (dataUrl) => {
    setImageDataUrl(dataUrl);
    setExtractedText('');
    setCameraOpen(false);
    stopAllAudio();
    if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
  };

  // ---------------- OCR ----------------
  const runOcr = async () => {
    if (!imageDataUrl) return;
    setBusyOcr(true);
    try {
      const { data } = await axios.post(
        `${API}/reader/ocr`,
        { image_base64: imageDataUrl, save_as_note: false },
        { withCredentials: true, timeout: 60000 },
      );
      const txt = (data?.text || '').trim();
      if (!txt) {
        toast.error('Hittade ingen text på bilden 🌸 Prova med bättre ljus eller skarpare bild.');
      } else {
        setExtractedText(txt);
        toast.success(`✨ Läste ${data.char_count} tecken`);
      }
    } catch (e) {
      toast.error(`Misslyckades: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyOcr(false);
    }
  };

  // ---------------- TTS (OpenAI) ----------------
  const generateOpenAITts = async () => {
    if (!extractedText.trim()) return;
    setBusyTts(true);
    stopAllAudio();
    try {
      const resp = await axios.post(
        `${API}/reader/tts`,
        { text: extractedText, voice: openaiVoice, model: openaiModel, speed: 1.0 },
        { withCredentials: true, responseType: 'blob', timeout: 90000 },
      );
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(resp.data);
      setAudioUrl(url);
      // Auto-play once ready
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.playbackRate = speed;
          audioRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (e) {
      toast.error(`Kunde inte skapa ljud: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyTts(false);
    }
  };

  // ---------------- Browser TTS ----------------
  const speakBrowser = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      toast.error('Webbläsaren stöder inte uppläsning 🌸');
      return;
    }
    const txt = extractedText.trim();
    if (!txt) return;
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setBrowserSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(txt);
    u.lang = 'sv-SE';
    u.rate = speed;
    u.onend = () => setBrowserSpeaking(false);
    u.onerror = () => setBrowserSpeaking(false);
    setBrowserSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  };

  const seekTo = (val) => {
    if (audioRef.current && Number.isFinite(val)) {
      audioRef.current.currentTime = val;
      setPosition(val);
    }
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-5" data-testid="reader-page">
      {/* Hero */}
      <div className="neo-card p-5 bg-gradient-to-br from-[#FFF5E6] to-[#FFE8F5]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#FFC8DD] border-2 border-black flex items-center justify-center">
            <Volume2 className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-['Fredoka']">Läs upp 📖</h2>
            <p className="text-sm text-gray-700">
              Ta foto eller ladda upp en bild på text — så läser vi upp den för dig 💛
            </p>
          </div>
        </div>
        {/* Mode sub-tabs */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => setMode('single')}
            className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ${mode === 'single' ? 'bg-[#FFC8DD]' : 'bg-white'}`}
            data-testid="reader-mode-single"
          >
            <FileText className="w-4 h-4" /> Enskild
          </button>
          <button
            onClick={() => setMode('queue')}
            className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ${mode === 'queue' ? 'bg-[#FFE8A3]' : 'bg-white'}`}
            data-testid="reader-mode-queue"
          >
            <ListMusic className="w-4 h-4" /> Kö 🎧
          </button>
        </div>
      </div>

      {mode === 'queue' ? (
        <ReaderQueue />
      ) : (<>

      {/* Step 1: Get the paper */}
      {!imageDataUrl && !extractedText && (
        <div className="neo-card p-6">
          <p className="text-sm font-bold mb-3">1. Ge mig pappret 📸</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => setCameraOpen(true)}
              className="neo-btn p-5 bg-[#A2D2FF] flex flex-col items-center gap-2"
              data-testid="reader-camera-btn"
            >
              <Camera className="w-8 h-8" />
              <span className="font-bold">Ta foto</span>
              <span className="text-xs">Använd kameran nu</span>
            </button>
            <button
              onClick={handlePickFile}
              className="neo-btn p-5 bg-[#B9FBC0] flex flex-col items-center gap-2"
              data-testid="reader-upload-btn"
            >
              <Upload className="w-8 h-8" />
              <span className="font-bold">Ladda upp bild</span>
              <span className="text-xs">Från album</span>
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/jpg"
            onChange={handleFileChange}
            className="hidden"
            data-testid="reader-file-input"
          />
        </div>
      )}

      {/* Step 2: Image preview + OCR */}
      {imageDataUrl && (
        <div className="neo-card p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold">2. Bild redo — läs in texten 🔍</p>
            <button
              onClick={() => { setImageDataUrl(null); setExtractedText(''); stopAllAudio(); if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); } }}
              className="neo-btn p-1.5 bg-white text-xs flex items-center gap-1"
              data-testid="reader-clear-image-btn"
            >
              <X className="w-3 h-3" /> Byt bild
            </button>
          </div>
          <div className="rounded-xl overflow-hidden border-2 border-black mb-3 max-h-[420px] flex items-center justify-center bg-gray-50">
            <img src={imageDataUrl} alt="paper" className="max-h-[420px] object-contain" />
          </div>
          <button
            onClick={runOcr}
            disabled={busyOcr}
            className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold flex items-center justify-center gap-2"
            data-testid="reader-ocr-btn"
          >
            {busyOcr ? <><Loader2 className="w-5 h-5 animate-spin" /> Läser av…</> : <><Sparkles className="w-5 h-5" /> Läs av texten</>}
          </button>
        </div>
      )}

      {/* Step 3: Read aloud */}
      {extractedText && (
        <div className="neo-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold">3. Texten är inläst — välj röst 🔊</p>
          </div>

          <textarea
            value={extractedText}
            onChange={(e) => setExtractedText(e.target.value)}
            rows={Math.min(14, Math.max(4, extractedText.split('\n').length + 1))}
            className="w-full neo-card p-3 text-sm bg-[#FFFEF5] focus:outline-none"
            data-testid="reader-text-area"
          />

          {/* Voice picker */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { stopAllAudio(); setVoiceMode('openai'); }}
              className={`neo-btn px-3 py-1.5 text-xs font-bold ${voiceMode === 'openai' ? 'bg-[#FFC8DD]' : 'bg-white'}`}
              data-testid="reader-voice-openai"
            >
              ✨ Mjuk röst (OpenAI)
            </button>
            <button
              onClick={() => { stopAllAudio(); setVoiceMode('browser'); }}
              className={`neo-btn px-3 py-1.5 text-xs font-bold ${voiceMode === 'browser' ? 'bg-[#A2D2FF]' : 'bg-white'}`}
              data-testid="reader-voice-browser"
            >
              💬 Webbläsarröst (gratis)
            </button>
          </div>

          {voiceMode === 'openai' && (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-bold mb-1.5">Röst</p>
                <div className="flex flex-wrap gap-1.5">
                  {OPENAI_VOICES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setOpenaiVoice(v.id)}
                      className={`neo-btn px-2 py-1 text-[11px] ${openaiVoice === v.id ? 'bg-[#FFE8A3]' : 'bg-white'}`}
                      title={v.desc}
                      data-testid={`reader-openai-voice-${v.id}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-bold">Kvalitet:</span>
                <button onClick={() => setOpenaiModel('tts-1')} className={`neo-btn px-2 py-0.5 ${openaiModel === 'tts-1' ? 'bg-[#B9FBC0]' : 'bg-white'}`}>Snabb</button>
                <button onClick={() => setOpenaiModel('tts-1-hd')} className={`neo-btn px-2 py-0.5 ${openaiModel === 'tts-1-hd' ? 'bg-[#B9FBC0]' : 'bg-white'}`}>HD</button>
              </div>
              <button
                onClick={generateOpenAITts}
                disabled={busyTts || !extractedText.trim()}
                className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold flex items-center justify-center gap-2"
                data-testid="reader-generate-tts-btn"
              >
                {busyTts ? <><Loader2 className="w-5 h-5 animate-spin" /> Skapar ljud…</> : <><Volume2 className="w-5 h-5" /> Läs upp för mig</>}
              </button>

              {audioUrl && (
                <AudioPlayer
                  audioRef={audioRef}
                  audioUrl={audioUrl}
                  playing={playing}
                  setPlaying={setPlaying}
                  duration={duration}
                  setDuration={setDuration}
                  position={position}
                  setPosition={setPosition}
                  speed={speed}
                  setSpeed={setSpeed}
                  onPlayPause={handlePlayPause}
                  onSeek={seekTo}
                  fmt={fmt}
                />
              )}
            </div>
          )}

          {voiceMode === 'browser' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="font-bold">Hastighet:</span>
                {SPEEDS.map((s) => (
                  <button key={s} onClick={() => setSpeed(s)} className={`neo-btn px-2 py-0.5 ${speed === s ? 'bg-[#A2D2FF]' : 'bg-white'}`} data-testid={`reader-browser-speed-${s}`}>
                    {s}x
                  </button>
                ))}
              </div>
              <button
                onClick={speakBrowser}
                disabled={!extractedText.trim()}
                className="neo-btn w-full py-3 bg-[#A2D2FF] font-bold flex items-center justify-center gap-2"
                data-testid="reader-browser-speak-btn"
              >
                {browserSpeaking ? <><Square className="w-5 h-5" /> Stoppa</> : <><Volume2 className="w-5 h-5" /> Läs upp</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* History intentionally removed — documents are never saved to history. */}

      {cameraOpen && (
        <CameraModal
          onClose={() => setCameraOpen(false)}
          onCapture={onCameraCaptured}
        />
      )}
      </>)}
    </div>
  );
}

// ---------------- Audio Player ----------------
function AudioPlayer({ audioRef, audioUrl, playing, setPlaying, duration, setDuration, position, setPosition, speed, setSpeed, onPlayPause, onSeek, fmt }) {
  return (
    <div className="neo-card p-3 bg-[#FFFEF5]">
      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onTimeUpdate={(e) => setPosition(e.target.currentTime || 0)}
        data-testid="reader-audio-element"
      />
      <div className="flex items-center gap-3">
        <button
          onClick={onPlayPause}
          className="neo-btn p-2 bg-[#FFC8DD]"
          data-testid="reader-audio-playpause"
          aria-label={playing ? 'Pausa' : 'Spela'}
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(0.01, duration)}
          step={0.1}
          value={position}
          onChange={(e) => onSeek(parseFloat(e.target.value))}
          className="flex-1 accent-[#FFC8DD]"
          data-testid="reader-audio-scrub"
        />
        <span className="text-[11px] tabular-nums w-16 text-right">{fmt(position)} / {fmt(duration)}</span>
      </div>
      <div className="flex items-center gap-1 mt-2 flex-wrap text-xs">
        <span className="font-bold mr-1">Hastighet:</span>
        {SPEEDS.map((s) => (
          <button
            key={s}
            onClick={() => setSpeed(s)}
            className={`neo-btn px-1.5 py-0.5 ${speed === s ? 'bg-[#FFC8DD]' : 'bg-white'}`}
            data-testid={`reader-audio-speed-${s}`}
          >
            {s}x
          </button>
        ))}
        <button
          onClick={() => { if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play(); } }}
          className="neo-btn px-1.5 py-0.5 ml-auto"
          data-testid="reader-audio-restart"
        >
          <RotateCcw className="w-3 h-3 inline" /> Start om
        </button>
      </div>
    </div>
  );
}

// ---------------- Camera Modal ----------------
function CameraModal({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');

  const startCam = useCallback(async (mode) => {
    setError(null);
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      setError(e?.message || 'Kunde inte starta kameran');
    }
  }, []);

  useEffect(() => {
    startCam(facingMode);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [facingMode, startCam]);

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(v, 0, 0, w, h);
    // Compress to 1600px JPEG for fast upload
    const maxEdge = 1600;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const tw = Math.round(w * scale);
    const th = Math.round(h * scale);
    if (scale < 1) {
      const c2 = document.createElement('canvas');
      c2.width = tw; c2.height = th;
      const c2x = c2.getContext('2d');
      c2x.drawImage(canvas, 0, 0, tw, th);
      onCapture(c2.toDataURL('image/jpeg', 0.85));
    } else {
      onCapture(canvas.toDataURL('image/jpeg', 0.85));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" data-testid="reader-camera-modal">
      <div className="neo-card bg-white p-3 w-full max-w-2xl">
        <div className="flex items-center justify-between mb-2">
          <p className="font-bold">📸 Ta foto av pappret</p>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="reader-camera-close"><X className="w-4 h-4" /></button>
        </div>
        {error ? (
          <div className="p-4 text-sm bg-[#FFE8E8] rounded-xl border-2 border-black">
            Kameran är blockerad: {error}. Tillåt åtkomst i webbläsarens inställningar.
          </div>
        ) : (
          <video ref={videoRef} className="w-full rounded-xl border-2 border-black bg-black aspect-video object-cover" muted playsInline />
        )}
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))}
            className="neo-btn p-2 bg-white text-xs"
            data-testid="reader-camera-flip"
          >
            🔄 Byt kamera
          </button>
          <button
            onClick={snap}
            disabled={!!error}
            className="neo-btn flex-1 py-3 bg-[#FFC8DD] font-bold"
            data-testid="reader-camera-snap"
          >
            📸 Ta bilden
          </button>
        </div>
      </div>
    </div>
  );
}
